import React, { useEffect, useMemo, useState } from 'react';
import { useAuthUser } from 'react-auth-kit';
import { useIntl } from 'react-intl';
import { Redirect } from 'react-router-dom';
import { useEditUserManagementMutation } from 'src/api/mutations/userManagementMutation';
import { useUserQuery } from 'src/api/queries/userManagementQueries';
import { useAlerts } from 'src/common/AlertManager';
import { CaretDown, CaretRight, Panel, Wrench } from 'src/common/icons';
import { Button, Input, Loader, PageTitle, Paper } from 'src/components';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useForm from 'src/hooks/useForm';
import PasswordSchema, { SetPasswordSchema } from 'src/schemas/password';
import UserSchema from 'src/schemas/user';
import { ValidationError } from 'yup';
import messages from './messages';

import styles from './styles.module.scss';
import BoardingTour from 'src/components/BoardingTour';
import { ConnectedAppsPanel, messages as connectedAppsMessages } from 'src/components/ConnectedApps';

const FoldableSection: React.FC<{ title: string }> = ({ title, children }) => {
  const [isRevealed, setIsRevealed] = useState(true);
  return (
    <div className={styles.foldableContainer}>
      <h4 onClick={() => setIsRevealed(!isRevealed)}>
        {isRevealed ? <CaretDown /> : <CaretRight />} {title}
      </h4>
      {isRevealed && <div>{children}</div>}
    </div>
  );
};

// Stable across renders: the query is keyed on it.
const SELF_SCOPE = { kind: 'self' } as const;

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const auth = useAuthUser();
  const authenticatedUser = auth();
  const { formatMessage } = useIntl();

  const { showSuccess, showAlert } = useAlerts();

  const { data: userData, isLoading } = useUserQuery(authenticatedUser?.userId);

  const { getErrorMessage } = useErrorMessages();

  const { form, setForm, handleChange, formErrors, clearFormErrors, setFieldError } = useForm({
    email: '',
    firstName: '',
    lastName: '',
  });

  const {
    form: passwordForm,
    handleChange: handlePasswordChange,
    formErrors: passwordFormErrors,
    setFieldError: setPasswordFieldError,
    clearFormErrors: clearPasswordFormErrors,
  } = useForm({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const editUserMutation = useEditUserManagementMutation(authenticatedUser?.userId, {
    onSuccess: () => {
      setIsEditing(false);

      showSuccess(formatMessage(messages.success));
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const hasChanges = useMemo(() => {
    if (!userData) return false;

    return (
      form.email !== userData.data.email ||
      form.firstName !== userData.data.first_name ||
      form.lastName !== userData.data.last_name
    );
  }, [userData, form]);

  const handleSaveChanges = () => {
    clearFormErrors();
    try {
      UserSchema.validateSync(form, { abortEarly: false });

      editUserMutation.mutateAsync({
        email: form.email,
        first_name: form.firstName,
        last_name: form.lastName,
      });
    } catch (error) {
      if (error instanceof ValidationError) {
        error.inner.forEach((err) => {
          if (!err.path) return;

          setFieldError(err.path, err.message);
        });
      }
    }
  };

  // false for an account created with Google / Microsoft that has not set a
  // password yet (server: UserDAO.has_password). Older API responses lack the
  // field; treat that as "has one" so the form is unchanged for them.
  const hasPassword = userData?.data?.has_password !== false;

  const handleSavePassword = () => {
    clearPasswordFormErrors();
    try {
      if (hasPassword) {
        PasswordSchema.validateSync(passwordForm, { abortEarly: false });
      } else {
        SetPasswordSchema.validateSync(passwordForm, { abortEarly: false });
      }

      editUserMutation.mutateAsync(
        hasPassword
          ? { old_password: passwordForm.oldPassword, new_password: passwordForm.newPassword }
          : { new_password: passwordForm.newPassword },
      );
    } catch (error) {
      if (error instanceof ValidationError) {
        error.inner.forEach((err) => {
          if (!err.path) return;

          setPasswordFieldError(err.path, err.message);
        });
      }
    }
  };

  useEffect(() => {
    if (!userData || !userData.data) return;

    setForm({
      email: userData.data.email,
      firstName: userData.data.first_name,
      lastName: userData.data.last_name,
    });
  }, [userData]);

  if (isLoading) return <Loader />;

  return (
     <><BoardingTour title='Profile' description='This page shows the profile details of the user' path='/me' uniqueId='PROFILE' />
    <section className={styles.pageWrapper}>
      <PageTitle
        title="Profile"
        rightContainer={
          isEditing ? (
            <div className={styles.groupButtons}>
              <Button size="small" mode="tertiary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button
                size="small"
                onClick={handleSaveChanges}
                loading={editUserMutation.isLoading}
                disabled={!hasChanges || editUserMutation.isLoading}
              >
                Save Changes
              </Button>
            </div>
          ) : (
            <Button icon={<Wrench />} size="small" onClick={() => setIsEditing(true)}>
              Edit Profile
            </Button>
          )
        }
      />

      <div className={styles.container}>
        <Paper>
          <div className={styles.userName}>
            <Input
              label="First Name"
              variant="secondary"
              disabled={!isEditing}
              value={form.firstName}
              onChange={(value) => handleChange('firstName', value)}
              error={formErrors.firstName}
            />
            <Input
              label="Last Name"
              variant="secondary"
              disabled={!isEditing}
              value={form.lastName}
              onChange={(value) => handleChange('lastName', value)}
              error={formErrors.lastName}
            />
          </div>
          <Input
            label="Email"
            variant="secondary"
            disabled={!isEditing}
            value={form.email}
            onChange={(value) => handleChange('email', value)}
            error={formErrors.email}
          />
        </Paper>

        <Paper>
          <FoldableSection title={hasPassword ? 'Update Password' : 'Set a Password'}>
            {!hasPassword && (
              <p className={styles.hint} data-test="set-password-hint">
                You signed in with Google or Microsoft, so this account has no password yet. Set one to also sign
                in with your email and password.
              </p>
            )}
            {hasPassword && (
              <Input
                label="Current Password"
                variant="secondary"
                type="password"
                value={passwordForm.oldPassword}
                onChange={(value) => handlePasswordChange('oldPassword', value)}
                error={passwordFormErrors.oldPassword}
              />
            )}
            <Input
              label="New Password"
              variant="secondary"
              type="password"
              value={passwordForm.newPassword}
              onChange={(value) => handlePasswordChange('newPassword', value)}
              error={passwordFormErrors.newPassword}
            />
            <Input
              label="Confirm Password"
              variant="secondary"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(value) => handlePasswordChange('confirmPassword', value)}
              error={passwordFormErrors.confirmPassword}
            />

            <div className={styles.actions}>
              <Button
                size="small"
                onClick={handleSavePassword}
                loading={editUserMutation.isLoading}
                disabled={editUserMutation.isLoading}
              >
                {hasPassword ? 'Save Password' : 'Set Password'}
              </Button>
            </div>
          </FoldableSection>
        </Paper>

        <Paper>
          <FoldableSection title={formatMessage(connectedAppsMessages.title)}>
            <ConnectedAppsPanel scope={SELF_SCOPE} showIntro />
          </FoldableSection>
        </Paper>
      </div>
    </section>
    </>
  );
};

export default Profile;
